import type { ReactNode } from "react";
import reactToText from "react-to-text";

import type { ToastProps } from "@/components/ui/toast";
import { parseError } from "@/utils/helpers";

const TOAST_DETAILS_COPY_LABEL = "Details";

const getTextFromNode = (node?: ReactNode) => {
  if (node == null || typeof node === "boolean") {
    return "";
  }

  if (typeof node === "string" || typeof node === "number") {
    return String(node).trim();
  }

  return reactToText(node).trim();
};

export const createToastErrorDetails = (error: unknown) => {
  const parsedError = parseError(error, { log: false });

  if (!parsedError) {
    return undefined;
  }

  return (
    <pre className="text-xs/5 wrap-break-word whitespace-pre-wrap">
      {parsedError}
    </pre>
  );
};

export const buildToastCopyText = ({
  title,
  description,
  details,
}: Pick<ToastProps, "title" | "description" | "details">) => {
  const titleText = getTextFromNode(title);
  const descriptionText = getTextFromNode(description);
  const detailsText = getTextFromNode(details);

  return [
    titleText,
    descriptionText,
    detailsText && `${TOAST_DETAILS_COPY_LABEL}:\n${detailsText}`,
  ]
    .filter(Boolean)
    .join("\n\n");
};

type ErrorToastOptions = Omit<ToastProps, "variant" | "details"> & {
  error: unknown;
  details?: ReactNode;
};

export const getErrorToastProps = ({
  title = "Error",
  description,
  error,
  details,
  ...props
}: ErrorToastOptions): ToastProps => ({
  ...props,
  title,
  description,
  variant: "destructive",
  details: details ?? createToastErrorDetails(error),
});
